import type { AppLanguage } from '../i18n/types';
import { parseCalendarDayKey } from '../utils/assetBookingCalendar';
import type { AssistantIntent } from './parseAssistantIntent';
import type { AssistantPriceConstraint } from './assistantPriceConstraint';
import { formatAssistantPriceChip } from './assistantPriceConstraint';
import type { AssistantPriceSort } from './parseAssistantPriceSort';
import {
  formatAssistantScheduleChip,
  isAssistantScheduleActive,
  type AssistantSchedule,
} from './assistantSchedule';

export type AssistantSearchChipParts = {
  date?: string | null;
  /** Soft month window `YYYY-MM` — shown instead of a single weekday. */
  month?: string | null;
  time?: string | null;
  street?: string | null;
  streetDisplay?: string | null;
  city?: string | null;
  venue?: string | null;
  open24h?: boolean;
  price?: AssistantPriceConstraint | null;
  priceSort?: AssistantPriceSort | null;
  proximityStreetDisplay?: string | null;
  schedule?: AssistantSchedule | null;
};

function formatMonthChip(monthYm: string, language: AppLanguage): string | null {
  const m = /^(\d{4})-(\d{2})$/.exec(monthYm.trim());
  if (!m) {
    return null;
  }
  const year = Number(m[1]);
  const monthIndex = Number(m[2]) - 1;
  if (!Number.isFinite(year) || monthIndex < 0 || monthIndex > 11) {
    return null;
  }
  return new Intl.DateTimeFormat(language === 'he' ? 'he-IL' : 'en-US', {
    month: 'long',
    year: 'numeric',
  }).format(new Date(year, monthIndex, 1));
}

function isPriceSort(value: string | null | undefined): value is AssistantPriceSort {
  return value === 'priceAsc' || value === 'priceDesc';
}

function priceSortChip(sort: AssistantPriceSort | null | undefined, language: AppLanguage): string | null {
  if (sort === 'priceAsc') {
    return language === 'he' ? 'מהזול ליקר' : 'cheapest first';
  }
  if (sort === 'priceDesc') {
    return language === 'he' ? 'מהיקר לזול' : 'most expensive first';
  }
  return null;
}

function shortCityLabel(city: string): string {
  return city.split(/\s*[-–]\s*/)[0]?.trim() || city.trim();
}

function weekdayShort(dateKey: string, language: AppLanguage): string | null {
  const parsed = parseCalendarDayKey(dateKey);
  if (!parsed) {
    return null;
  }
  const date = new Date(parsed.year, parsed.monthIndex, parsed.day);
  return new Intl.DateTimeFormat(language === 'he' ? 'he-IL' : 'en-US', {
    weekday: 'short',
  }).format(date);
}

/** Compact “Wed 14:30 · יגאל אלון · תל אביב” chip for assistant search. */
export function formatAssistantSearchChip(
  parts: AssistantSearchChipParts,
  language: AppLanguage = 'en',
): string | null {
  const bits: string[] = [];
  if (parts.open24h) {
    bits.push(language === 'he' ? 'גישה 24/7' : '24/7 access');
  }
  const priceChip = formatAssistantPriceChip(parts.price, language);
  if (priceChip) {
    bits.push(priceChip);
  }
  const scheduleChip = formatAssistantScheduleChip(parts.schedule, language);
  if (scheduleChip) {
    bits.push(scheduleChip);
  } else {
    const monthChip = parts.month ? formatMonthChip(parts.month, language) : null;
    const weekday = !monthChip && parts.date ? weekdayShort(parts.date, language) : null;
    const time = parts.time?.trim() || '';
    if (monthChip) {
      bits.push(monthChip);
    } else if (weekday && time) {
      bits.push(`${weekday} ${time}`);
    } else if (weekday) {
      bits.push(weekday);
    } else if (time) {
      bits.push(time);
    }
  }
  const venue = parts.venue?.trim();
  if (venue) {
    bits.push(venue);
  }
  const street = (parts.streetDisplay ?? parts.street)?.trim();
  if (street && street !== venue) {
    bits.push(street);
  }
  const city = parts.city?.trim() ? shortCityLabel(parts.city) : '';
  if (city && city !== street) {
    bits.push(city);
  }
  const sortChip = priceSortChip(parts.priceSort, language);
  if (sortChip) {
    bits.push(sortChip);
  }
  const near = parts.proximityStreetDisplay?.trim();
  if (near) {
    bits.push(language === 'he' ? `קרוב ל${near}` : `near ${near}`);
  }
  return bits.length > 0 ? bits.join(' · ') : null;
}

export function assistantSearchChipFromIntent(
  intent: AssistantIntent,
  language: AppLanguage = 'en',
): string | null {
  if (intent.kind !== 'search') {
    return null;
  }
  if (
    !intent.whenDate &&
    !intent.whenMonth &&
    !intent.whenTime &&
    !intent.street &&
    !intent.open24h &&
    !intent.venue &&
    !intent.price &&
    !intent.priceSort &&
    !intent.proximityStreet &&
    !isAssistantScheduleActive(intent.schedule)
  ) {
    return null;
  }
  return formatAssistantSearchChip(
    {
      date: intent.whenDate,
      month: intent.whenMonth,
      time: intent.whenTime,
      street: intent.street,
      streetDisplay: intent.streetDisplay,
      city: intent.city,
      venue: intent.venue,
      open24h: intent.open24h,
      price: intent.price,
      priceSort: intent.priceSort,
      proximityStreetDisplay: intent.proximityStreetDisplay ?? intent.proximityStreet,
      schedule: intent.schedule,
    },
    language,
  );
}

export function assistantSearchChipFromBrowse(
  params: {
    city?: string | null;
    whenDate?: string | null;
    whenMonth?: string | null;
    whenTime?: string | null;
    street?: string | null;
    open24h?: boolean;
    price?: AssistantPriceConstraint | null;
    listSort?: string | null;
    schedule?: AssistantSchedule | null;
  },
  language: AppLanguage = 'en',
): string | null {
  if (
    !params.whenDate &&
    !params.whenMonth &&
    !params.whenTime &&
    !params.street &&
    !params.open24h &&
    !params.price &&
    !isPriceSort(params.listSort) &&
    !isAssistantScheduleActive(params.schedule)
  ) {
    return null;
  }
  return formatAssistantSearchChip(
    {
      date: params.whenDate,
      month: params.whenMonth,
      time: params.whenTime,
      street: params.street,
      city: params.city,
      open24h: params.open24h,
      price: params.price,
      priceSort: isPriceSort(params.listSort) ? params.listSort : null,
      schedule: params.schedule,
    },
    language,
  );
}
